import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '@/types';
import { authService } from '@/services/auth';
import { websocketService } from '@/services/websocket';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());

  // Fetch current user from backend
  const { data: user, isLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => apiClient.getCurrentUser(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  // Register
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState<Error | null>(null);

  const register = useCallback(async (data: { name: string; email: string; password: string }) => {
    setRegisterLoading(true);
    setRegisterError(null);
    try {
      await apiClient.register(data);
      setIsAuthenticated(true);
      navigate('/');
    } catch (err) {
      setRegisterError(err instanceof Error ? err : new Error('Registration failed'));
    } finally {
      setRegisterLoading(false);
    }
  }, [navigate]);

  // Logout
  const logout = useCallback(() => {
    authService.logout();
    websocketService.disconnect();
    setIsAuthenticated(false);
    queryClient.clear();
    navigate('/login');
  }, [navigate, queryClient]);

  // Check permission
  const hasPermission = useCallback((permission: string): boolean => {
    return authService.hasPermission(permission);
  }, []);

  // Initialize WebSocket on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      websocketService.connect();
    }
  }, [isAuthenticated]);

  return {
    user: user || authService.getUser(),
    isAuthenticated,
    isLoading,
    logout,
    hasPermission,
    register,
    registerLoading,
    registerError,
  };
}
